import type { BranchRepresentationType } from '@readme/api/src/mappings/branch/types';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import useClassy from '@core/hooks/useClassy';
import useIsDocumentReady from '@core/hooks/useIsDocumentReady';
import useUniqueId from '@core/hooks/useUniqueId';
import { useSuperHubStore } from '@core/store';
import type { APIErrorType } from '@core/types';

import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Modal, { ModalBody, ModalFooter } from '@ui/Modal';
import { notify } from '@ui/Notification';
import Truncate from '@ui/Truncate';

import { useBlockingModalContext } from '..';
import VersionNotification from '../../components/Notification';
import useBuildBranchPath from '../../hooks/useBuildBranchPath';

import { useDeleteBranchModalContext } from './Context';
import styles from './index.module.scss';

function SuccessNotification({ deletedBranch }: { deletedBranch: BranchRepresentationType }) {
  return (
    <VersionNotification
      description={
        <>
          <Truncate maxWidth={100}>v{deletedBranch.name}</Truncate> has been deleted.
        </>
      }
      kind="success"
      title="Branch Deleted"
    />
  );
}

export default function DeleteBranchModal() {
  const bem = useClassy(styles, 'DeleteBranchModal');
  const uid = useUniqueId('DeleteBranchModal');
  const modalContainerId = uid('modal');
  const modalRef = useRef<Modal>(null);
  const isClient = useIsDocumentReady();

  const buildVersionPath = useBuildBranchPath();
  const [currentBranchName, deleteBranch] = useSuperHubStore(s => [s.currentBranch.name, s.branches.deleteBranch]);
  const { branchToDelete, setBranchToDelete } = useDeleteBranchModalContext();
  const { setBlockingModalValue } = useBlockingModalContext();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<APIErrorType | null>(null);

  const sourceBranchName = useMemo(() => `v${branchToDelete?.name}`, [branchToDelete?.name]);
  const externalDiffUrl = useMemo(() => branchToDelete?.href.external.diff || '', [branchToDelete?.href.external.diff]);

  useEffect(() => {
    modalRef.current?.toggle(!!branchToDelete);

    // When closing the modal, reset all other state.
    if (!branchToDelete) {
      setIsDeleting(false);
      setDeleteError(null);
    }
  }, [branchToDelete]);

  const handleDelete = useCallback(async () => {
    if (!branchToDelete) return;
    try {
      setIsDeleting(true);
      await deleteBranch(branchToDelete.name);
      setBranchToDelete(null);
      notify(<SuccessNotification deletedBranch={branchToDelete} />);

      // Redirect to parent version after deleting the current branch.
      if (currentBranchName === branchToDelete.name) {
        setBlockingModalValue('Redirecting…');
        window.location.assign(buildVersionPath(branchToDelete.base.name));
      }
    } catch (error) {
      setDeleteError(error);
    } finally {
      setIsDeleting(false);
    }
  }, [branchToDelete, buildVersionPath, currentBranchName, deleteBranch, setBranchToDelete, setBlockingModalValue]);

  return (
    <>
      <Modal
        ref={modalRef}
        className={bem('-container')}
        noDismiss
        size="sm"
        target={`#${modalContainerId}`}
        verticalCenter
      >
        <ModalBody className={bem('-body')}>
          <h1 className={bem('-title')}>Deleting Branch</h1>
          <p className={bem('-description')}>
            Are you sure you want to delete <Truncate maxWidth="20ch">{sourceBranchName}</Truncate>?
            {!!externalDiffUrl && (
              <>
                {' '}
                This will also delete your branch in{' '}
                <a className={bem('-diff-link')} href={externalDiffUrl} rel="noreferrer" target="_blank">
                  GitHub
                  <Icon aria-hidden="true" name="arrow-up-right" />
                </a>
                .
              </>
            )}
          </p>

          {!!deleteError && (
            <div className={bem('-error')}>
              <span>Error occurred when deleting. Try again?</span>
              <Flex align="center" gap={0} layout="col">
                {deleteError.info?.errors?.map(error => (
                  <span key={error.key}>{error.message.endsWith('.') ? error.message : `${error.message}.`}</span>
                ))}
              </Flex>
            </div>
          )}
        </ModalBody>

        <ModalFooter align="center" gap="sm" justify="center">
          <Button disabled={isDeleting} ghost kind="minimum" onClick={() => setBranchToDelete(null)}>
            Cancel
          </Button>
          <Button
            aria-label="Delete"
            kind="destructive"
            loading={isDeleting ? 'Deleting' : false}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </ModalFooter>
      </Modal>

      {isClient
        ? createPortal(
            <div
              className={bem('&', 'ModalWrapper')}
              data-color-mode="dark"
              data-testid={bem()}
              id={modalContainerId}
            />,
            document.body,
          )
        : null}
    </>
  );
}

export * from './Context';
